// Markdown export. Tree-walks the shapes and emits a flat outline:
//
//   # frame-{boardId} — {date}
//
//   ## {Frame label A}
//   - sticky text
//   - sticky text
//
//   ## {Frame label B}
//   - sticky text
//
//   ## Notes
//   - free sticky (not inside any frame)
//   - free text
//
//   ## Action items
//   - [ ] sticky tagged 'action'
//
// Rules of the walk:
//   - Frames become H2 headings in y/x order (top-to-bottom, left-to-right).
//   - Stickies / text shapes whose center sits inside a frame's bbox land
//     under that frame's heading.
//   - Stickies / text outside any frame collect into "Notes".
//   - Anything tagged ACTION_TAG (Sticky.tags includes 'action') lands in
//     "Action items" with a checkbox prefix — and is omitted from the
//     other sections so it's listed exactly once.
//   - Items within a section are ordered by y/x within that section.
//   - Empty sections are omitted.

import type { Frame, Shape, Sticky } from '@frame/shared';
import { shapeBBox } from '../canvas/binding/shapesMap';
import { isActionItem } from '../retro/actionItems';

export interface MarkdownExportOpts {
  // Heading shown on line 1. Defaults to the board id.
  title?: string;
  // ISO date string for the second-line subtitle. Default = today.
  date?: Date;
}

interface TextBearing {
  shape: Shape;
  text: string;
  centerX: number;
  centerY: number;
}

const ACTIONS_SECTION = 'Action items';
const NOTES_SECTION = 'Notes';

export function exportMarkdown(
  shapes: readonly Shape[],
  opts: MarkdownExportOpts = {},
): string {
  const title = opts.title ?? 'Board';
  const date = (opts.date ?? new Date()).toISOString().slice(0, 10);

  const frames: Frame[] = [];
  const items: TextBearing[] = [];
  const actions: TextBearing[] = [];

  for (const shape of shapes) {
    if (shape.type === 'frame') {
      frames.push(shape);
      continue;
    }
    if (shape.type !== 'sticky' && shape.type !== 'text' && shape.type !== 'rect' && shape.type !== 'ellipse') {
      continue;
    }
    const text = readText(shape);
    if (!text) continue;
    const bbox = shapeBBox(shape);
    if (!bbox) continue;
    const entry: TextBearing = {
      shape,
      text,
      centerX: bbox.x + bbox.w / 2,
      centerY: bbox.y + bbox.h / 2,
    };
    if (shape.type === 'sticky' && isActionItem(shape)) {
      actions.push(entry);
    } else {
      items.push(entry);
    }
  }

  // Order frames top-to-bottom, then left-to-right within the same row.
  frames.sort((a, b) => a.y - b.y || a.x - b.x);
  items.sort((a, b) => a.centerY - b.centerY || a.centerX - b.centerX);
  actions.sort((a, b) => a.centerY - b.centerY || a.centerX - b.centerX);

  // Bucket items into frames by center-in-bbox test. Items outside every
  // frame become "Notes".
  const byFrame = new Map<string, TextBearing[]>();
  const notes: TextBearing[] = [];
  for (const it of items) {
    const owner = frames.find((f) => pointInFrame(it.centerX, it.centerY, f));
    if (owner) {
      const arr = byFrame.get(owner.id) ?? [];
      arr.push(it);
      byFrame.set(owner.id, arr);
    } else {
      notes.push(it);
    }
  }

  const lines: string[] = [];
  lines.push(`# ${title} — ${date}`);
  lines.push('');

  for (const frame of frames) {
    const bucket = byFrame.get(frame.id);
    if (!bucket || bucket.length === 0) continue;
    lines.push(`## ${frame.label || '(untitled frame)'}`);
    for (const it of bucket) {
      lines.push(`- ${escape(it.text)}`);
    }
    lines.push('');
  }

  if (notes.length > 0) {
    lines.push(`## ${NOTES_SECTION}`);
    for (const it of notes) {
      lines.push(`- ${escape(it.text)}`);
    }
    lines.push('');
  }

  if (actions.length > 0) {
    lines.push(`## ${ACTIONS_SECTION}`);
    for (const it of actions) {
      lines.push(`- [ ] ${escape(it.text)}`);
    }
    lines.push('');
  }

  // Trim any trailing blank lines so the file doesn't end with two newlines.
  while (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();
  return lines.join('\n') + '\n';
}

function readText(shape: Shape): string {
  if ('text' in shape && typeof shape.text === 'string') return shape.text.trim();
  return '';
}

function pointInFrame(x: number, y: number, frame: Frame): boolean {
  return x >= frame.x && x <= frame.x + frame.w && y >= frame.y && y <= frame.y + frame.h;
}

// Multi-line strings get joined with two spaces + newline so they render
// as line breaks inside the bullet. Stray backticks are not escaped —
// users intentionally use them for code, and the markdown round-trips.
function escape(s: string): string {
  return s.replace(/\n/g, '  \n  ');
}

// Convenience wrapper that wraps the markdown string in a Blob suitable
// for triggerDownload.
export function markdownBlob(markdown: string): Blob {
  return new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
}
